(() => {
  const register = (name, initializer) => {
    if (window.SafeWalkV51Product?.register) window.SafeWalkV51Product.register(name, initializer);
    else (window.SafeWalkV51ProductQueue ||= []).push([name, initializer]);
  };

  register('cart', ({ root, listen, addCleanup }) => {
    const purchase = root.swv5Purchase;
    if (!purchase) return false;
    const {
      form,
      idInput,
      kaching,
      hideAccessoryAddons,
      giftMode,
      cartGiftVariantId,
      cartGiftProductId,
      cartGiftRequired,
      realGiftsRequired,
      realGiftIdentities,
      normalizeColor,
      normalizeSize,
      discountedLinePrice
    } = purchase;
    if (!form) return;
    const kachingContract = window.SafeWalkV51KachingContract;
    let allowKachingSubmit = false;
    let previewToken = 0;
    let previewTimer = null;
    let activeCartObserver = null;
    let activeCartRecovery = null;
    const successTimers = new Set();
    const schedule = (callback, delay) => {
      const timer = window.setTimeout(() => {
        successTimers.delete(timer);
        callback();
      }, delay);
      successTimers.add(timer);
      return timer;
    };
    const waitForTwoFrames = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    const findKachingRadio = (barId) => {
      if (!barId) return null;
      return Array.from(root.querySelectorAll('input[name^="kaching-bundles-deal"]')).find((input) => input.value === barId) || null;
    };
    const waitForKachingRadio = (barId, timeoutMs = 8000) => {
      const immediate = findKachingRadio(barId);
      if (immediate) return Promise.resolve(immediate);
      return new Promise((resolve) => {
        let finished = false;
        const finish = (radio) => {
          if (finished) return;
          finished = true;
          observer.disconnect();
          window.clearTimeout(timer);
          resolve(radio || null);
        };
        const observer = new MutationObserver(() => {
          const radio = findKachingRadio(barId);
          if (radio) finish(radio);
        });
        observer.observe(document.documentElement, { childList: true, subtree: true });
        const timer = window.setTimeout(() => finish(null), timeoutMs);
      });
    };
    const kachingSelectSelector = '.kaching-bundles__bar-variant-select.kaching-bundles-option';
    const findKachingBarRoot = (barId) => findKachingRadio(barId)?.closest('.kaching-bundles__bar') || null;
    const waitForKachingSelects = async (barId, expectedCount, timeoutMs = 8000) => {
      const deadline = Date.now() + timeoutMs;
      while (Date.now() <= deadline) {
        const radio = findKachingRadio(barId);
        const bar = findKachingBarRoot(barId);
        const selects = bar ? Array.from(bar.querySelectorAll(kachingSelectSelector)) : [];
        if (radio?.checked && selects.length === expectedCount) return { radio, bar, selects };
        await waitForTwoFrames();
      }
      throw new Error('kaching-variant-controls');
    };
    const findKachingOption = (select, desiredValue, normalizer) => {
      const desired = normalizer(desiredValue);
      if (!desired) return null;
      return Array.from(select.options).find((option) => {
        if (option.disabled || !String(option.value || '').trim()) return false;
        return [option.value, option.label, option.textContent].some((value) => normalizer(value) === desired);
      }) || null;
    };
    const readKachingDeal = () => kachingContract?.readDeal(root) || null;
    const controlPlanFor = (state, desiredColor, desiredSize) => {
      if (!kachingContract) throw new Error('kaching-contract-runtime');
      return kachingContract.controlPlan({
        deal: readKachingDeal(),
        state,
        desiredColor,
        desiredSize,
        normalizeColor,
        normalizeSize,
        kaching
      });
    };
    const setKachingSelect = async (barId, expectedCount, index, desiredValue, normalizer) => {
      const snapshot = await waitForKachingSelects(barId, expectedCount);
      const select = snapshot.selects[index];
      if (!select) throw new Error('kaching-variant-position');
      const option = findKachingOption(select, desiredValue, normalizer);
      if (!option) throw new Error('kaching-variant-option');
      select.value = option.value;
      select.dispatchEvent(new Event('input', { bubbles: true }));
      select.dispatchEvent(new Event('change', { bubbles: true }));
      await waitForTwoFrames();
      const updated = await waitForKachingSelects(barId, expectedCount);
      const updatedSelect = updated.selects[index];
      const updatedOption = updatedSelect?.options[updatedSelect.selectedIndex];
      const verified = updatedOption && [updatedOption.value, updatedOption.label, updatedOption.textContent]
        .some((value) => normalizer(value) === normalizer(desiredValue));
      if (!verified) throw new Error('kaching-variant-verification');
    };
    const syncKachingVariantControls = async (state, desiredColor, desiredSize) => {
      const plan = controlPlanFor(state, desiredColor, desiredSize);
      if (!plan || !desiredColor || !desiredSize) throw new Error('kaching-variant-plan');
      for (const [index, [desiredValue, normalizer]] of plan.entries()) {
        await setKachingSelect(state.barId, plan.length, index, desiredValue, normalizer);
      }
      const finalState = await waitForKachingSelects(state.barId, plan.length);
      plan.forEach(([desiredValue, normalizer], index) => {
        const select = finalState.selects[index];
        const selectedOption = select?.options[select.selectedIndex];
        const verified = selectedOption && [selectedOption.value, selectedOption.label, selectedOption.textContent]
          .some((value) => normalizer(value) === normalizer(desiredValue));
        if (!verified) throw new Error('kaching-variant-final-verification');
      });
      if (!idInput) throw new Error('missing-variant-input');
      idInput.value = String(state.mainVariant.id);
      const restoredVariant = purchase.findVariant();
      if (!restoredVariant || Number(restoredVariant.id) !== Number(state.mainVariant.id) || Number(idInput.value) !== Number(state.mainVariant.id)) {
        throw new Error('kaching-main-variant-restore');
      }
      return finalState.radio;
    };
    const runPreviewSync = async (token) => {
      if (hideAccessoryAddons || purchase.isBusy()) return;
      const selectedVariant = purchase.findVariant();
      const selectedState = purchase.resolvePackage(selectedVariant, purchase.selectedPackageKey());
      const desiredColor = purchase.currentColor();
      const desiredSize = purchase.currentSize();
      const radio = await waitForKachingRadio(selectedState.barId);
      if (!radio || token !== previewToken || purchase.isBusy()) return;
      if (!radio.checked) {
        radio.click();
        await waitForTwoFrames();
      }
      try {
        await syncKachingVariantControls(selectedState, desiredColor, desiredSize);
      } catch (error) {
        root.dataset.kachingPreviewStatus = 'error';
        root.dataset.kachingPreviewError = error instanceof Error ? error.message : 'unknown';
        return;
      }
      root.dataset.kachingPreviewStatus = 'ready';
      delete root.dataset.kachingPreviewError;
      if (selectedVariant && idInput) idInput.value = String(selectedVariant.id);
      if (token !== previewToken || purchase.isBusy() || purchase.selectedPackageKey() !== selectedState.kind) return;
      purchase.updatePackageSurfaces(purchase.resolvePackage(selectedVariant, selectedState.kind));
    };
    const syncKachingPreviewSelection = () => {
      if (hideAccessoryAddons) return;
      const token = ++previewToken;
      window.clearTimeout(previewTimer);
      previewTimer = window.setTimeout(() => runPreviewSync(token), 80);
    };
    const validateKachingConfig = (state) => Boolean(kachingContract?.validateConfig({
      deal: readKachingDeal(),
      state,
      kaching,
      cartGiftProductId,
      realGiftIdentities,
      giftMode
    }));
    const validateKachingItems = (element, state) => Boolean(kachingContract?.validateItems({
      element,
      state,
      kaching,
      cartGiftVariantId,
      realGiftIdentities,
      giftMode
    }));
    const syncKachingForm = (kForm, state, submittedProperties = {}) => {
      kForm.querySelectorAll('[data-swv5-proxy-property]').forEach((node) => node.remove());
      let kachingIdInput = kForm === form ? idInput : kForm.querySelector('input[name="id"]');
      if (!kachingIdInput) {
        kachingIdInput = document.createElement('input');
        kachingIdInput.type = 'hidden';
        kachingIdInput.name = 'id';
        kForm.appendChild(kachingIdInput);
      }
      kachingIdInput.value = String(state.mainVariant.id);
      kachingIdInput.dispatchEvent(new Event('change', { bubbles: true }));
      const properties = new Map();
      if (kForm !== form) new FormData(form).forEach((value, key) => {
        if (key.startsWith('properties[') && value) properties.set(key, String(value));
      });
      Object.entries(submittedProperties).forEach(([key, value]) => {
        if (value) properties.set(`properties[${key}]`, String(value));
      });
      properties.forEach((value, key) => {
        const existing = Array.from(kForm.elements).find((input) => input.name === key);
        if (existing) { existing.value = value; return; }
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = key;
        input.value = value;
        input.dataset.swv5ProxyProperty = 'true';
        kForm.appendChild(input);
      });
    };
    const cartAddRequestUrl = (input) => {
      if (typeof input === 'string') return input;
      if (input instanceof URL) return input.href;
      return input && typeof input === 'object' && 'url' in input ? input.url : '';
    };
    const isCartAddRequest = (input) => {
      try {
        const url = new URL(cartAddRequestUrl(input), window.location.origin);
        return /\/cart\/add(?:\.js)?\/?$/.test(url.pathname);
      } catch (error) {
        return false;
      }
    };
    const observeNextCartAdd = (timeoutMs = 15000) => {
      const originalFetch = window.fetch;
      let timer;
      let finished = false;
      let resolveResult;
      const promise = new Promise((resolve) => { resolveResult = resolve; });
      const cleanup = () => {
        window.clearTimeout(timer);
        if (window.fetch === wrappedFetch) window.fetch = originalFetch;
      };
      const finish = (result) => {
        if (finished) return;
        finished = true;
        cleanup();
        resolveResult(result);
      };
      const wrappedFetch = async function(...args) {
        const watchesCartAdd = isCartAddRequest(args[0]);
        try {
          const response = await originalFetch.apply(this, args);
          if (watchesCartAdd) {
            let body = null;
            try {
              body = await response.clone().json();
            } catch (error) {
              body = null;
            }
            finish({ ok: response.ok, status: response.status, body });
          }
          return response;
        } catch (error) {
          if (watchesCartAdd) finish({ ok: false, status: 0, body: null });
          throw error;
        }
      };
      window.fetch = wrappedFetch;
      timer = window.setTimeout(() => finish({ ok: false, status: 0, body: null, timeout: true }), timeoutMs);
      return { promise, cancel: () => finish({ ok: false, status: 0, body: null, cancelled: true }) };
    };
    const cartAddResponseMatches = (result, state, groupId, submittedProperties) => {
      return Boolean(window.SafeWalkV51CartResponse?.matches({
        result, state, groupId, submittedProperties, giftMode, kaching,
        cartGiftVariantId, realGiftIdentities, discountedLinePrice
      }));
    };
    const closeCartDrawerFallback = (drawer) => {
      if (!drawer) return;
      drawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden-tablet');
    };
    const ensureCartDrawerFallbackMethods = (drawer) => {
      if (!drawer) return null;
      drawer.close = () => closeCartDrawerFallback(drawer);
      return drawer;
    };
    const openCartDrawerFallback = () => {
      const drawer = ensureCartDrawerFallbackMethods(document.querySelector('cart-drawer.drawer'));
      if (!drawer) return false;
      drawer.classList.add('active');
      drawer.classList.remove('is-empty');
      document.body.classList.add('overflow-hidden');
      const overlay = drawer.querySelector('#CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', drawer.close, { once: true });
      drawer.querySelectorAll('.drawer__close').forEach((button) => button.addEventListener('click', drawer.close, { once: true }));
      drawer.querySelector('.drawer__inner')?.focus?.();
      return true;
    };
    const replaceCartDrawerFromHTML = (html) => {
      if (!html) return false;
      const currentDrawer = ensureCartDrawerFallbackMethods(document.querySelector('cart-drawer.drawer'));
      if (!currentDrawer) return false;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const freshDrawer = parsed.querySelector('cart-drawer.drawer, cart-drawer');
      if (!freshDrawer) return false;
      currentDrawer.innerHTML = freshDrawer.innerHTML;
      currentDrawer.className = freshDrawer.className;
      currentDrawer.classList.remove('is-empty');
      ensureCartDrawerFallbackMethods(currentDrawer);
      return true;
    };
    const replaceCartIconFromHTML = (html) => {
      if (!html) return;
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const fresh = parsed.querySelector('#cart-icon-bubble');
      const current = document.querySelector('#cart-icon-bubble');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    };
    const renderCartFallbackSections = async (sections = {}) => {
      if (sections['cart-drawer']) {
        replaceCartDrawerFromHTML(sections['cart-drawer']);
      } else {
        try {
          const response = await fetch(`${window.location.pathname}?sections=cart-drawer`, { headers: { Accept: 'application/json' } });
          if (response.ok) {
            const sectionState = await response.json();
            replaceCartDrawerFromHTML(sectionState['cart-drawer']);
          }
        } catch (error) {
          return false;
        }
      }
      if (sections['cart-icon-bubble']) replaceCartIconFromHTML(sections['cart-icon-bubble']);
      return openCartDrawerFallback();
    };
    const finishSuccess = () => {
      purchase.setBusy(false);
      purchase.setSuccess(false);
    };
    const emitResult = (ok, status, state, groupId) => {
      root.dispatchEvent(new CustomEvent('swv5:cart-add-result', {
        bubbles: true,
        detail: { ok, status, package: state.kind, expectedTotalCents: state.total, groupId }
      }));
    };

    listen(root, 'swv5:selection-change', syncKachingPreviewSelection);
    listen(document, 'kaching-bundles-initialized', syncKachingPreviewSelection);
    listen(root, 'click', (event) => {
      const submitButton = event.target.closest('[data-swv5-submit]');
      if (!submitButton || !root.contains(submitButton)) return;
      purchase.setActiveSubmitter(submitButton);
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitButton);
      else {
        const submitEvent = typeof SubmitEvent === 'function'
          ? new SubmitEvent('submit', { bubbles: true, cancelable: true, submitter: submitButton })
          : new Event('submit', { bubbles: true, cancelable: true });
        form.dispatchEvent(submitEvent);
      }
    }, true);
    listen(form, 'submit', async (event) => {
      if (allowKachingSubmit) {
        allowKachingSubmit = false;
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      purchase.setActiveSubmitter(event.submitter);
      if (purchase.isBusy()) return;
      const selectedVariant = purchase.findVariant();
      const submittedState = purchase.resolvePackage(selectedVariant, purchase.selectedPackageKey());
      purchase.updatePackageSurfaces(submittedState);
      purchase.clearError();
      if (!selectedVariant || !submittedState.available || Number(idInput?.value) !== Number(selectedVariant.id)) {
        purchase.showErrorStatus('missingPackage');
        return;
      }
      if (!hideAccessoryAddons && cartGiftRequired && cartGiftVariantId <= 0) {
        purchase.showErrorStatus('missingGift');
        return;
      }
      const realGiftRuntime = window.SafeWalkV6RealGifts;
      const realGiftConfig = realGiftRuntime?.configFromRoot?.(root);
      const personalizationValues = giftMode === 'personalized_real_gifts'
        ? purchase.personalization()
        : {};
      const preliminaryPersonalization = realGiftRuntime?.splitPersonalization?.(giftMode, personalizationValues)
        ?.personalization || {};
      const personalizationValidator = realGiftRuntime?.validationKey;
      const personalizationError = typeof personalizationValidator === 'function'
        ? personalizationValidator(preliminaryPersonalization)
        : 'validation';
      if (giftMode === 'personalized_real_gifts' && personalizationError) {
        purchase.showErrorStatus('incompletePersonalization');
        return;
      }
      if (!hideAccessoryAddons && realGiftsRequired && !realGiftRuntime?.validateConfig(realGiftConfig)) {
        purchase.showErrorStatus('missingGift');
        return;
      }
      const submittedGroupId = purchase.createGroupId();
      const submittedSplit = realGiftRuntime?.splitPersonalization
        ? realGiftRuntime.splitPersonalization(giftMode, { ...purchase.properties(), ...personalizationValues })
        : { lineProperties: purchase.properties(), personalization: {} };
      const submittedProperties = {
        ...submittedSplit.lineProperties,
        ...(giftMode === 'personalized_real_gifts'
          ? realGiftRuntime.mainContractProperties(realGiftConfig, submittedGroupId)
          : {})
      };
      const submittedPersonalization = submittedSplit.personalization;
      purchase.setBusy(true);

      if (!hideAccessoryAddons) {
        let cartResult = null;
        let submissionStarted = false;
        try {
          const radio = await waitForKachingRadio(submittedState.barId);
          if (!radio) throw new Error('kaching-radio');
          if (!validateKachingConfig(submittedState)) throw new Error('kaching-config-drift');
          radio.click();
          await waitForTwoFrames();
          const syncedRadio = await syncKachingVariantControls(submittedState, purchase.currentColor(), purchase.currentSize());
          if (!syncedRadio?.checked) throw new Error('kaching-selection');
          const kForm = syncedRadio.form;
          if (!kForm) throw new Error('kaching-form');
          const submitter = kForm.querySelector('button[name="add"][type="submit"], input[name="add"][type="submit"]');
          if (!submitter) throw new Error('kaching-submit');
          syncKachingForm(kForm, submittedState, submittedProperties);
          await waitForTwoFrames();
          const kachingElement = root.querySelector('kaching-bundles-block[deal-block]');
          const canValidateKachingItems = Boolean(kachingContract?.supportsItemValidation?.(kachingElement));
          if (canValidateKachingItems && !validateKachingItems(kachingElement, submittedState)) throw new Error('kaching-item-selection');
          root.dataset.kachingItemsPreflight = canValidateKachingItems ? 'verified' : 'post-submit';
          if (Number(idInput?.value) !== Number(selectedVariant.id) || Number(purchase.findVariant()?.id) !== Number(selectedVariant.id)) {
            throw new Error('kaching-main-variant');
          }
          root.dataset.kachingBridgeReady = 'true';
          root.dataset.kachingBarId = submittedState.barId;
          root.dataset.kachingCartStatus = 'pending';
          delete root.dataset.kachingCartError;
          activeCartObserver = observeNextCartAdd();
          allowKachingSubmit = true;
          submitter.disabled = false;
          submissionStarted = true;
          if (typeof kForm.requestSubmit === 'function') kForm.requestSubmit(submitter);
          else submitter.click();
          submitter.disabled = true;
          if (kForm !== form) allowKachingSubmit = false;
          schedule(() => { allowKachingSubmit = false; }, 0);
          activeCartRecovery = window.SafeWalkV51KachingRecovery?.recover({
            groupId: submittedGroupId,
            matches: (candidate) => cartAddResponseMatches(candidate, submittedState, submittedGroupId, submittedProperties)
          }) || null;
          const recoveryMatch = activeCartRecovery
            ? activeCartRecovery.promise.then((outcome) => outcome.matched
              ? outcome.result
              : new Promise(() => {}))
            : new Promise(() => {});
          cartResult = await Promise.race([activeCartObserver.promise, recoveryMatch]);
          if (!cartAddResponseMatches(cartResult, submittedState, submittedGroupId, submittedProperties) && activeCartRecovery) {
            const recoveryOutcome = await activeCartRecovery.promise;
            if (recoveryOutcome.matched) cartResult = recoveryOutcome.result;
            else if (recoveryOutcome.ambiguous) throw new Error('cart-add-ambiguous');
          }
          activeCartRecovery?.cancel();
          activeCartRecovery = null;
          activeCartObserver?.cancel();
          activeCartObserver = null;
          root.dataset.kachingCartHttpStatus = String(cartResult.status || 0);
          if (!cartAddResponseMatches(cartResult, submittedState, submittedGroupId, submittedProperties)) {
            throw new Error(`cart-add-${cartResult.status || 'timeout'}`);
          }
          if (giftMode === 'personalized_real_gifts') {
            const reconciled = await realGiftRuntime.reconcileGroup({
              root,
              groupId: submittedGroupId,
              personalization: submittedPersonalization
            });
            if (!reconciled?.ok) {
              const reconcileError = new Error(reconciled?.reason || 'gift-reconciliation');
              reconcileError.safewalkRolledBack = reconciled?.rolledBack === true;
              throw reconcileError;
            }
          }
          root.dataset.kachingCartStatus = 'success';
          purchase.setSuccess(true);
          purchase.setStatus('added');
          emitResult(true, cartResult.status, submittedState, submittedGroupId);
          schedule(finishSuccess, 2200);
        } catch (error) {
          activeCartObserver?.cancel();
          activeCartObserver = null;
          activeCartRecovery?.cancel();
          activeCartRecovery = null;
          allowKachingSubmit = false;
          root.dataset.kachingBridgeReady = 'false';
          const ambiguousCart = submissionStarted && error?.safewalkRolledBack !== true;
          root.dataset.kachingCartStatus = ambiguousCart ? 'unknown' : 'error';
          root.dataset.kachingCartHttpStatus = String(cartResult?.status || 0);
          root.dataset.kachingCartError = error instanceof Error ? error.message : 'unknown';
          purchase.setSuccess(false);
          purchase.showErrorStatus('error');
          if (!ambiguousCart) purchase.setBusy(false);
          emitResult(false, cartResult?.status || 0, submittedState, submittedGroupId);
        }
        return;
      }

      const cartDrawer = document.querySelector('cart-drawer');
      const drawerSections = cartDrawer && typeof cartDrawer.getSectionsToRender === 'function'
        ? cartDrawer.getSectionsToRender().map((section) => section.id)
        : ['cart-drawer', 'cart-icon-bubble'];
      const payload = { items: [{ id: Number(selectedVariant.id), quantity: 1, properties: submittedProperties }] };
      if (drawerSections.length) {
        payload.sections = drawerSections;
        payload.sections_url = window.location.pathname;
      }
      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(payload)
        });
        if (!response.ok) throw new Error('cart');
        const parsedState = await response.json();
        purchase.setSuccess(true);
        purchase.setStatus('added');
        if (cartDrawer && typeof cartDrawer.renderContents === 'function' && parsedState.sections) {
          cartDrawer.classList.remove('is-empty');
          cartDrawer.renderContents(parsedState);
        } else {
          await renderCartFallbackSections(parsedState.sections || {});
        }
        emitResult(true, response.status, submittedState, submittedGroupId);
        schedule(finishSuccess, 2200);
      } catch (error) {
        purchase.setSuccess(false);
        purchase.showErrorStatus('error');
        purchase.setBusy(false);
        emitResult(false, 0, submittedState, submittedGroupId);
      }
    }, true);
    syncKachingPreviewSelection();

    return () => {
      window.clearTimeout(previewTimer);
      successTimers.forEach((timer) => window.clearTimeout(timer));
      successTimers.clear();
      activeCartObserver?.cancel();
      activeCartObserver = null;
      activeCartRecovery?.cancel();
      activeCartRecovery = null;
      allowKachingSubmit = false;
    };
  });
})();
